import { useState, useEffect, useRef, useCallback } from 'react';
import Box           from '@mui/material/Box';
import Typography    from '@mui/material/Typography';
import Dialog        from '@mui/material/Dialog';
import DialogTitle   from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button        from '@mui/material/Button';
import Tooltip       from '@mui/material/Tooltip';
import AddAPhotoOutlinedIcon from '@mui/icons-material/AddAPhotoOutlined';
import CloseIcon             from '@mui/icons-material/Close';
import AddIcon               from '@mui/icons-material/Add';
import ChevronLeftIcon  from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AccessTimeIcon   from '@mui/icons-material/AccessTime';

import RankControl from '../../../shared/components/RankControl';
import FileUpload  from '../../../shared/components/FileUpload';
import { InlineAlert, useToast } from '../../../shared/components/status';
import { apiFetch, friendlyMessage } from '../../../api/apiClient';

import {
  GREEN, GREEN_HOVER, CHARCOAL, BORDER, MUTED, MUTED_FAINT, STONE50,
  labelMono, bodyFont, pageTitle, pageTitleAccent, pageDescription, PAGE_PX,
} from '../../../theme/tokens2';

// ─── Colour tokens (used by both shortlists) ──────────────────────────────────

export const GREEN_FAINT = 'rgba(33,131,51,0.05)';
export const GREEN_GRID  = 'rgba(33,131,51,0.35)';

// Undo accent — shown only on hover over an already-marked toggle, to make
// the destructive "remove" affordance legible without repurposing GREEN
// (which means "save"/"confirmed" everywhere else in the app).
export const DANGER       = '#B42318';
export const DANGER_FAINT = 'rgba(180,35,24,0.08)';

// ─── Material types ───────────────────────────────────────────────────────────
// ONE list, used by Materials and Sample Delivery. The `id` values must match
// cat_materials in the database exactly ("Wall Paint", not "Paint"). Before,
// each page had its own list and Sample Delivery spelled it "Paint", so wall
// paint samples were counted but never shown.
export const MATERIAL_TYPES = [
  { id: 'Tiles',      label: 'Tiles'      },
  { id: 'Vinyl',      label: 'Vinyl'      },
  { id: 'Laminate',   label: 'Laminate'   },
  { id: 'Wall Paint', label: 'Wall Paint' },
];

// How many samples of each type fit in one delivery box.
export const SAMPLE_QUOTAS = { Tiles: 25, Vinyl: 10, Laminate: 8, 'Wall Paint': 4 };

// ─── Sorting & grouping helpers ───────────────────────────────────────────────

export const sortItems = (data) =>
  [...data].sort((a, b) => {
    const catCmp = (a.subcategory || '').localeCompare(b.subcategory || '');
    return catCmp !== 0 ? catCmp : a.ranking - b.ranking;
  });

export const groupBySubcategory = (data) =>
  data.reduce((acc, item) => {
    const key = item.subcategory || 'Uncategorised';
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

// Moves one item up (-1) or down (+1) in a list and renumbers the ranks 1, 2, 3…
// Returns a NEW list of NEW objects, so React notices the change and nothing
// else that holds the old objects is changed by accident.
export function moveInList(list, index, direction) {
  const target = index + direction;
  if (target < 0 || target >= list.length) return null;
  const next = list.map((it) => ({ ...it }));
  [next[index], next[target]] = [next[target], next[index]];
  next.forEach((it, i) => { it.ranking = i + 1; });
  return next;
}

// ─── useRankSync ─────────────────────────────────────────────────────────────
// Saves a new order to the server, but only after the customer actually
// moves something (before, the pages also saved every time a list loaded).
//
//   const queueRankSave = useRankSync('/api/MaterialsShortlist/bulk');
//   … after a move: queueRankSave(listWithNewRankings);
//
// Several quick moves are combined into one save, sent 0.5 seconds after the
// last move. If the customer leaves the page before then, the save is sent
// straight away (keepalive lets it finish even after the page closes).
export function useRankSync(path) {
  const toast   = useToast();
  const timer   = useRef(null);
  const pending = useRef(new Map()); // id → ranking, newest wins

  const flush = useCallback(async ({ keepalive = false } = {}) => {
    clearTimeout(timer.current);
    timer.current = null;
    if (pending.current.size === 0) return;
    const body = [...pending.current].map(([id, ranking]) => ({ id, ranking }));
    pending.current = new Map();
    try {
      await apiFetch(path, { method: 'PUT', body, keepalive });
    } catch (err) {
      if (!keepalive) toast.error("We couldn't save your new order. Please try moving it again.");
    }
  }, [path, toast]);

  const queue = useCallback((list) => {
    list.forEach(({ id, ranking }) => pending.current.set(id, ranking));
    clearTimeout(timer.current);
    timer.current = setTimeout(() => flush(), 500);
  }, [flush]);

  // Leaving the page with a save still waiting: send it now.
  useEffect(() => () => { flush({ keepalive: true }); }, [flush]);

  return queue;
}

// ─── PrefOverlay ──────────────────────────────────────────────────────────────
// The rank buttons placed in the top-right corner of a photo card.

export function PrefOverlay({ onMoveUp, onMoveDown, onRemove, isFirst, isLast, cardHovered, itemLabel }) {
  return (
    <Box
      sx={{ position: 'absolute', top: 6, right: 6, zIndex: 2 }}
      onClick={(e) => e.stopPropagation()}
    >
      <RankControl
        variant="overlay"
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
        onRemove={onRemove}
        isFirst={isFirst}
        isLast={isLast}
        cardHovered={cardHovered}
        itemLabel={itemLabel}
      />
    </Box>
  );
}

// ─── ScrollableTabStrip ───────────────────────────────────────────────────────
//
// Wraps a horizontal row of tab buttons (passed in as `children`) with a
// scroll-fade + arrow-button frame, so a long row of tabs is never stuck
// off-screen with no way to reach it. Each screen renders its own tab
// buttons; this component only owns the scrolling shell around them.
export function ScrollableTabStrip({ children }) {
  const scrollRef = useRef(null);
  const [canLeft,  setCanLeft]  = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateEdges = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 2);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  useEffect(() => {
    updateEdges();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateEdges, { passive: true });
    const ro = new ResizeObserver(updateEdges);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', updateEdges); ro.disconnect(); };
    // Re-measure whenever the tab list itself changes length/content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateEdges, children]);

  const nudge = (dir) => scrollRef.current?.scrollBy({ left: dir * 180, behavior: 'smooth' });

  const arrowSx = (side) => ({
    position: 'absolute', [side]: 0, top: 0, bottom: 0,
    width: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'none', border: 'none', cursor: 'pointer',
    color: MUTED_FAINT, zIndex: 3,
    '&:hover': { color: GREEN },
  });

  return (
    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      {canLeft && (
        <>
          <Box sx={{
            position: 'absolute', left: 0, top: 0, bottom: 0, width: 28, zIndex: 2,
            background: 'linear-gradient(to right, #fff 25%, transparent)',
            pointerEvents: 'none',
          }} />
          <Box component="button" type="button" aria-label="Scroll tabs left" onClick={() => nudge(-1)} sx={arrowSx('left')}>
            <ChevronLeftIcon sx={{ fontSize: '0.95rem' }} />
          </Box>
        </>
      )}

      <Box
        ref={scrollRef}
        sx={{
          display: 'flex', alignItems: 'center', overflowX: 'auto',
          scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
          px: canLeft || canRight ? '18px' : 0,
        }}
      >
        {children}
      </Box>

      {canRight && (
        <>
          <Box sx={{
            position: 'absolute', right: 0, top: 0, bottom: 0, width: 28, zIndex: 2,
            background: 'linear-gradient(to left, #fff 25%, transparent)',
            pointerEvents: 'none',
          }} />
          <Box component="button" type="button" aria-label="Scroll tabs right" onClick={() => nudge(1)} sx={arrowSx('right')}>
            <ChevronRightIcon sx={{ fontSize: '0.95rem' }} />
          </Box>
        </>
      )}
    </Box>
  );
}

// ─── GhostCard ────────────────────────────────────────────────────────────────
// The dashed "+ Add" card at the end of a grid.

export function GhostCard({ onClick, aspectRatio = '4/5', label = 'Add option' }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        width: '100%', p: 0,
        border: `1.5px dashed ${hovered ? GREEN : 'rgba(0,0,0,0.22)'}`,
        borderRadius: '3px',
        aspectRatio,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: '7px',
        cursor: 'pointer',
        color: hovered ? GREEN : 'rgba(0,0,0,0.55)',
        background: hovered ? 'rgba(33,131,51,0.06)' : 'rgba(0,0,0,0.025)',
        transition: 'all 0.15s ease',
        '&:focus-visible': { outline: `2px solid ${GREEN}`, outlineOffset: '2px' },
      }}
    >
      <AddAPhotoOutlinedIcon sx={{ fontSize: '1.25rem' }} />
      <Typography sx={{ ...labelMono, fontSize: '8px', fontWeight: 700 }}>{label}</Typography>
    </Box>
  );
}

// ─── ShortlistToggleButton ────────────────────────────────────────────────────
//
// Shared save/mark toggle. Used both by the Lightbox's candidate "Save to
// Shortlist" button and by MaterialCard's "Add to samples" row — the same
// interaction: a persistent on/off toggle living in a card footer.
//   • rest         — actionable; tinted or solid depending on `variant`
//   • marked       — soft green confirmation, label switches to markedLabel
//   • marked+hover — flips to a danger tint + removeLabel, so hovering warns
//                    you that clicking again undoes the action
//   • busy         — desaturated fill + busy copy while the request runs
//
// variant: 'solid' (one primary action in a short list) | 'tint' (dense grids)
//
// Props: isMarked, onToggle(nextValue), unmarkedLabel, markedLabel, removeLabel,
//        busyMarkLabel, busyRemoveLabel, variant, sx
export function ShortlistToggleButton({
  isMarked,
  onToggle,
  unmarkedLabel,
  markedLabel,
  removeLabel,
  busyMarkLabel   = 'Saving…',
  busyRemoveLabel = 'Removing…',
  variant = 'tint',
  sx = {},
}) {
  const [hovered, setHovered] = useState(false);
  const [busy,    setBusy]    = useState(false);

  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onToggle(!isMarked);
    } finally {
      setBusy(false);
    }
  };

  const showRemove = isMarked && hovered && !busy;

  const label = busy
    ? (isMarked ? busyRemoveLabel : busyMarkLabel)
    : showRemove
      ? removeLabel
      : isMarked
        ? markedLabel
        : unmarkedLabel;

  const isSolid = variant === 'solid';

  const background = busy
    ? (isMarked ? 'rgba(180,35,24,0.45)' : 'rgba(33,131,51,0.45)')
    : isMarked
      ? (hovered ? DANGER_FAINT : 'rgba(33,131,51,0.08)')
      : isSolid
        ? (hovered ? GREEN_HOVER : GREEN)
        : (hovered ? 'rgba(33,131,51,0.12)' : GREEN_FAINT);

  const color = busy
    ? '#fff'
    : isMarked
      ? (hovered ? DANGER : GREEN)
      : isSolid
        ? '#fff'
        : GREEN;

  return (
    <Box
      component="button"
      type="button"
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      disabled={busy}
      aria-pressed={!!isMarked}
      aria-label={isMarked ? removeLabel : unmarkedLabel}
      sx={{
        width: '100%', border: 'none',
        cursor: busy ? 'wait' : 'pointer',
        py: '7px', minHeight: 30,
        borderTop: `0.5px solid ${isMarked ? 'rgba(33,131,51,0.25)' : BORDER}`,
        backgroundColor: background,
        ...labelMono, fontSize: '9px',
        color,
        transition: 'background-color 0.15s ease, color 0.15s ease',
        '&:focus-visible': { outline: `2px solid ${GREEN}`, outlineOffset: '-2px' },
        ...sx,
      }}
    >
      {label}
    </Box>
  );
}

// ─── PageHeader ───────────────────────────────────────────────────────────────
// Shared header for Moodboard, Materials and Fittings & Furnishings.
//   title       — first part of the headline, e.g. "Rank your"
//   accent      — green italic ending of the headline, e.g. "materials."
//   description — one or two sentences on what to do here
//   note        — optional small green line (e.g. the 12pm promise)
//   children    — the tab strip that sits under the header
export function PageHeader({ title, accent, description, note, children }) {
  return (
    <Box sx={{
      background: '#fff', borderBottom: `0.5px solid ${BORDER}`,
      px: PAGE_PX, pt: '20px', pb: 0, flexShrink: 0,
    }}>
      <Typography component="h1" sx={pageTitle}>
        {title}
        {accent && (
          <>
            {' '}
            <Box component="span" sx={pageTitleAccent}>{accent}</Box>
          </>
        )}
      </Typography>

      {description && (
        <Typography sx={pageDescription}>
          {description}
        </Typography>
      )}

      {note && (
        <Typography sx={{
          ...bodyFont, fontSize: '12px', color: GREEN, mt: '10px',
          display: 'flex', alignItems: 'center', gap: '6px',
        }}>
          <AccessTimeIcon sx={{ fontSize: '0.875rem' }} />
          {note}
        </Typography>
      )}

      <Box sx={{ mt: '14px' }}>{children}</Box>
    </Box>
  );
}

// ─── AddCategoryChip ──────────────────────────────────────────────────────────

export function AddCategoryChip({ onAdd, placeholder = 'Category name…', label = 'Add' }) {
  const [open,  setOpen]  = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  const handleOpen = () => {
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleConfirm = () => {
    const trimmed = draft.trim();
    if (trimmed) onAdd(trimmed);
    setDraft('');
    setOpen(false);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter')  handleConfirm();
    if (e.key === 'Escape') { setDraft(''); setOpen(false); }
  };

  if (open) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <Box
          component="input"
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKey}
          onBlur={() => { setOpen(false); setDraft(''); }}
          placeholder={placeholder}
          aria-label={placeholder}
          maxLength={32}
          sx={{
            border: `0.5px solid ${GREEN}`,
            borderRadius: '2px',
            px: '8px', py: '4px',
            ...labelMono, fontSize: '8px', color: CHARCOAL,
            background: '#fff', outline: 'none',
            width: 120,
            '&::placeholder': { color: 'rgba(0,0,0,0.25)' },
          }}
        />
        <Box
          component="button"
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleConfirm}
          sx={{
            ...labelMono, fontSize: '8px', color: '#fff',
            background: GREEN, border: 'none', cursor: 'pointer',
            px: '8px', py: '4px', borderRadius: '2px',
            '&:hover': { background: GREEN_HOVER },
          }}
        >
          Add
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component="button"
      type="button"
      onClick={handleOpen}
      sx={{
        display: 'flex', alignItems: 'center', gap: '3px',
        ...labelMono, fontSize: '8px', color: MUTED_FAINT,
        border: '0.5px dashed rgba(0,0,0,0.18)',
        borderRadius: '2px',
        background: 'transparent', cursor: 'pointer',
        px: '10px', py: '4px',
        transition: 'color 0.15s, border-color 0.15s',
        '&:hover': { color: GREEN, borderColor: 'rgba(33,131,51,0.4)' },
      }}
    >
      <AddIcon sx={{ fontSize: '0.65rem' }} />
      {label}
    </Box>
  );
}

// ─── Removing rooms and categories ───────────────────────────────────────────
// The pattern (from "Removing rooms and categories UX recommendations"):
//   • a small × on the tab or chip itself — always shown on the selected one,
//     on others only on hover or keyboard focus (and not at all on touch
//     screens, so a tap meant to switch tabs can't remove one by accident);
//   • the Delete key removes a tab or chip that has keyboard focus;
//   • empty → removed straight away, with "Undo" in a message for 6 seconds;
//   • not empty → RemoveConfirmDialog first, listing the real counts.

// Styles for the box that holds a tab/chip AND its ×, controlling when the
// × shows. `selected` = this is the active tab or chip.
export const removableSx = (selected) => ({
  display: 'flex', alignItems: 'center', flexShrink: 0,
  '& .rm-x': { opacity: selected ? 1 : 0, pointerEvents: selected ? 'auto' : 'none' },
  '&:hover .rm-x, &:focus-within .rm-x': { opacity: 1, pointerEvents: 'auto' },
  '@media (hover: none)': { '& .rm-x': { display: selected ? 'flex' : 'none' } },
});

// The × itself: 24 × 24 so it's easy to hit. Keyboard users use the Delete
// key on the tab instead, so the × is skipped when tabbing through.
// disabledReason: e.g. "You need at least one room." — shown as a tooltip,
// and the × does nothing.
export function RemoveButton({ label, onClick, disabledReason, busy = false }) {
  const disabled = !!disabledReason || busy;
  const button = (
    <Box
      component="button"
      type="button"
      className="rm-x"
      tabIndex={-1}
      aria-label={label}
      aria-disabled={disabled || undefined}
      onClick={(e) => { e.stopPropagation(); if (!disabled) onClick(); }}
      sx={{
        width: 24, height: 24, p: 0, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'none', border: 'none', borderRadius: '2px',
        color: MUTED_FAINT,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'opacity 0.15s, color 0.15s, background-color 0.15s',
        ...(disabled ? { opacity: '0.35 !important' } : {
          '&:hover': { color: DANGER, background: DANGER_FAINT },
        }),
        ...(busy ? { cursor: 'progress' } : {}),
      }}
    >
      <CloseIcon sx={{ fontSize: '0.75rem' }} />
    </Box>
  );
  return disabledReason
    ? <Tooltip title={disabledReason} placement="bottom">{button}</Tooltip>
    : button;
}

// The confirm box. Nothing is removed until the customer presses the red
// button. "Cancel" has the focus, and Esc cancels.
//   title        "Remove Kitchen?"
//   intro        "This also removes everything saved in this room:"
//   items        ["4 inspiration photos", "7 saved materials (2 marked for samples)"]
//   warnings     extra lines for things that are hard to replace (shown in brown)
//   notes        plain extra lines (e.g. "Rooms are shared by Moodboard and Materials…")
//   confirmLabel "Remove room"
export function RemoveConfirmDialog({
  open, title, intro, items = [], warnings = [], notes = [],
  confirmLabel = 'Remove', busy = false, error = '', onCancel, onConfirm,
}) {
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
      aria-labelledby="remove-confirm-title"
      aria-describedby="remove-confirm-body"
      PaperProps={{ sx: { borderRadius: '3px' } }}
    >
      <DialogTitle id="remove-confirm-title" sx={{ fontSize: '1.05rem', fontWeight: 600, color: CHARCOAL, pb: 1, ...bodyFont }}>
        {title}
      </DialogTitle>
      <DialogContent id="remove-confirm-body">
        {intro && (
          <Typography sx={{ fontSize: '0.875rem', color: CHARCOAL, lineHeight: 1.6, ...bodyFont }}>
            {intro}
          </Typography>
        )}
        {items.length > 0 && (
          <Box component="ul" sx={{ m: 0, mt: '6px', pl: '20px' }}>
            {items.map((line) => (
              <Typography component="li" key={line} sx={{ fontSize: '0.875rem', color: CHARCOAL, lineHeight: 1.7, fontWeight: 500, ...bodyFont }}>
                {line}
              </Typography>
            ))}
          </Box>
        )}
        {warnings.length > 0 && (
          <Box sx={{ mt: '14px' }}>
            <InlineAlert severity="warning">
              {warnings.map((line) => (
                <Box key={line} component="span" sx={{ display: 'block', '& + &': { mt: '4px' } }}>{line}</Box>
              ))}
            </InlineAlert>
          </Box>
        )}
        {notes.map((line) => (
          <Typography key={line} sx={{ fontSize: '0.8125rem', color: MUTED, lineHeight: 1.6, mt: '12px', ...bodyFont }}>
            {line}
          </Typography>
        ))}
        {error && <Box sx={{ mt: '14px' }}><InlineAlert severity="error">{error}</InlineAlert></Box>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          autoFocus
          onClick={onCancel}
          disabled={busy}
          sx={{ ...labelMono, fontSize: '10px', color: CHARCOAL, minHeight: 40, px: 2 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={onConfirm}
          disabled={busy}
          sx={{
            ...labelMono, fontSize: '10px', minHeight: 40, px: 2.5,
            background: DANGER, color: '#fff', borderRadius: '2px',
            '&:hover': { background: '#912018' },
            '&.Mui-disabled': { background: 'rgba(180,35,24,0.45)', color: '#fff' },
          }}
        >
          {busy ? 'Removing…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// "1 photo" / "3 photos"
export const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

// ─── ShortlistChipStrip ───────────────────────────────────────────────────────
// onRemoveCategory(name): when given, every chip except "All" gets a ×
// (Fittings & Furnishings). Materials' fixed material types don't.

export function ShortlistChipStrip({
  allCategories,
  setAllCategories,
  activeChip,
  setActiveChip,
  grouped,
  totalCount,
  addChipPlaceholder,
  allowAdd = true,
  onRemoveCategory,
}) {
  return (
    <Box sx={{
      display: 'flex', gap: '6px', alignItems: 'center',
      px: PAGE_PX, py: '10px', flexShrink: 0,
      overflowX: 'auto', scrollbarWidth: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
    }}>
      {[
        { label: `All (${totalCount})`, value: 'all' },
        ...allCategories.map((c) => ({
          label: grouped[c]?.length ? `${c} (${grouped[c].length})` : c,
          value: c,
        })),
      ].map(({ label, value }) => {
        const selected  = activeChip === value;
        const removable = !!onRemoveCategory && value !== 'all';
        return (
          <Box
            key={value}
            sx={{
              ...removableSx(selected),
              border: `1px solid ${selected ? GREEN : 'rgba(0,0,0,0.18)'}`,
              borderRadius: '2px',
              background: selected ? 'rgba(33,131,51,0.08)' : '#fff',
              transition: 'all 0.15s ease',
            }}
          >
            <Box
              component="button"
              type="button"
              aria-pressed={selected}
              aria-keyshortcuts={removable ? 'Delete' : undefined}
              onClick={() => setActiveChip(value)}
              onKeyDown={removable ? (e) => {
                if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); onRemoveCategory(value); }
              } : undefined}
              sx={{
                ...labelMono, fontSize: '9px', fontWeight: 700,
                pl: '10px', pr: removable ? '2px' : '10px', py: '5px',
                border: 'none', background: 'none',
                color: selected ? GREEN : CHARCOAL,
                opacity: selected ? 1 : 0.7,
                cursor: 'pointer', whiteSpace: 'nowrap',
                '&:focus-visible': { outline: `2px solid ${GREEN}`, outlineOffset: '2px' },
              }}
            >
              {label}
            </Box>
            {removable && (
              <RemoveButton label={`Remove ${value}`} onClick={() => onRemoveCategory(value)} />
            )}
          </Box>
        );
      })}

      {allowAdd && (
        <AddCategoryChip
          placeholder={addChipPlaceholder}
          onAdd={(name) => {
            const normalised = name.trim();
            if (!allCategories.includes(normalised)) {
              setAllCategories((prev) => [...prev, normalised]);
            }
            setActiveChip(normalised);
          }}
        />
      )}
    </Box>
  );
}

// ─── ShortlistSectionHeader ───────────────────────────────────────────────────

export function ShortlistSectionHeader({ label, count }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', py: '8px' }}>
      <Typography component="h2" sx={{ ...labelMono, fontSize: '9px', fontWeight: 700, color: CHARCOAL, whiteSpace: 'nowrap' }}>
        {label}{count > 0 ? ` — ${count} option${count !== 1 ? 's' : ''}` : ''}
      </Typography>
      <Box sx={{ flex: 1, height: '1px', background: 'rgba(0,0,0,0.12)' }} />
    </Box>
  );
}

// ─── ShortlistEmptyState ──────────────────────────────────────────────────────

export function ShortlistEmptyState({ message = 'Select a category and add your first option.' }) {
  return (
    <Box sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, textAlign: 'center' }}>
      <Typography sx={{ ...labelMono, fontSize: '9px', color: MUTED_FAINT }}>
        Nothing here yet
      </Typography>
      <Typography sx={{ fontSize: '0.875rem', color: MUTED, ...bodyFont }}>
        {message}
      </Typography>
    </Box>
  );
}

// ─── AddPhotoDialog ───────────────────────────────────────────────────────────

export function AddPhotoDialog({ open, categoryName, onClose, onUploaded, bucketName, filenameLabel }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: '3px' } }}
    >
      <DialogTitle sx={{ ...labelMono, fontSize: '10px', pb: 1 }}>
        Add to {categoryName}
      </DialogTitle>
      <DialogContent>
        <FileUpload
          bucketName={bucketName}
          allowedTypes={['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/avif', 'image/webp']}
          accept="image/*"
          filename_label={filenameLabel}
          onFileUpload={onUploaded}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          sx={{
            ...labelMono, fontSize: '9px', color: MUTED,
            border: `0.5px solid ${BORDER}`, borderRadius: '2px',
            px: 2, py: 0.75, textTransform: 'none',
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── ShortlistHintStrip ───────────────────────────────────────────────────────

export function ShortlistHintStrip({ message }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: '8px',
      mx: PAGE_PX, mb: '16px', px: '10px', py: '7px',
      background: GREEN_FAINT, borderLeft: `2px solid ${GREEN_GRID}`,
    }}>
      <Typography sx={{ fontSize: '10px', color: MUTED, lineHeight: 1.55, ...bodyFont }}>
        {message}
      </Typography>
    </Box>
  );
}

// ─── ItemDetailModal ──────────────────────────────────────────────────────────
//
// Props:
//   item        — the item object
//   open        — boolean
//   onClose     — () => void
//   onUpdated   — (updatedItem) => void
//   apiEndpoint — function (id) => string, e.g. (id) => `/api/furnitureshortlist/${id}`
//   fields      — array of { label, field, placeholder, type?, textarea?,
//                            prefix?, step?, maxLength? }
//                 type: 'number' → saved as a number (or null when empty);
//                       anything else is saved as text
//                 prefix: a fixed label in front of the box, e.g. 'SGD'
//                 step / maxLength: passed to the <input>
//                 for savvy proposals, all fields except 'notes' are read-only

export function ItemDetailModal({ item, open, onClose, onUpdated, apiEndpoint, fields }) {
  const [form,      setForm]      = useState({});
  const [saving,    setSaving]    = useState(false);
  const [saved,     setSaved]     = useState(false);
  const [saveError, setSaveError] = useState('');
  const savedTimerRef = useRef(null);

  const inputSx = {
    width: '100%', boxSizing: 'border-box',
    border: `1px solid ${BORDER}`,
    background: STONE50,
    padding: '7px 10px',
    fontSize: '12px',
    color: CHARCOAL,
    ...bodyFont,
    outline: 'none',
    transition: 'border-color .2s, background .2s',
    '&:focus': { borderColor: GREEN, background: '#fff' },
  };

  const miniLabel = {
    ...labelMono, fontSize: '8px', color: MUTED, display: 'block', mb: '4px',
  };

  // Populate form whenever the item changes.
  useEffect(() => {
    setForm(
      fields.reduce((acc, { field, type }) => {
        acc[field] = type === 'number'
          ? (item[field] != null ? String(item[field]) : '')
          : (item[field] || '');
        return acc;
      }, {})
    );
    setSaveError('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);

  useEffect(() => {
    return () => { if (savedTimerRef.current) clearTimeout(savedTimerRef.current); };
  }, []);

  // Savvy proposals: all fields except notes are read-only.
  const isFieldLocked = (field) => item.savvyproposal && field !== 'notes';

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    // Number fields are sent as numbers (null when empty or not a number).
    // Everything else, including a material price like "4.60 / sqft", is text.
    const body = fields.reduce((acc, { field, type }) => {
      if (type === 'number') {
        const n = Number(form[field]);
        acc[field] = form[field] === '' || !Number.isFinite(n) ? null : n;
      } else {
        acc[field] = form[field];
      }
      return acc;
    }, {});
    try {
      const updated = await apiFetch(apiEndpoint(item.id), { method: 'PUT', body });
      onUpdated(updated);
      setSaved(true);
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setSaveError(friendlyMessage(err, "We couldn't save your changes. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: '3px' } }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 2.5, pt: 2, pb: 1,
      }}>
        <Typography sx={{ ...labelMono, fontSize: '10px', color: CHARCOAL }}>
          {item.savvyproposal ? 'Savvy pick' : 'Edit item'}
        </Typography>
        <Box
          component="button"
          type="button"
          aria-label="Close"
          onClick={onClose}
          sx={{ background: 'none', border: 'none', cursor: 'pointer', color: MUTED, display: 'flex' }}
        >
          <CloseIcon sx={{ fontSize: '1rem' }} />
        </Box>
      </Box>

      <DialogContent sx={{ pt: 0, display: 'flex', gap: 3, overflow: 'hidden' }}>
        {/* Left: image */}
        <Box sx={{ width: 260, flexShrink: 0 }}>
          <Box
            component="img"
            src={item.imagesrc}
            alt={item.namelabel || ''}
            sx={{ width: '100%', aspectRatio: '4/5', objectFit: 'cover', borderRadius: '2px', display: 'block' }}
          />
          {item.savvyproposal && (
            <Box sx={{
              mt: '8px', px: '8px', py: '6px',
              background: 'rgba(33,131,51,0.05)',
              border: '0.5px solid rgba(33,131,51,0.20)',
              borderRadius: '2px',
            }}>
              <Typography sx={{ ...labelMono, fontSize: '8px', color: GREEN }}>
                Savvy pick — read-only fields are set by us. You can still add your own notes.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Right: fields */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
          {fields.map(({ label, field, placeholder, type, textarea, prefix, step, maxLength }) => {
            const locked = isFieldLocked(field);
            const inputId = `item-${item.id}-${field}`;
            const prefixId = `${inputId}-prefix`;
            const input = (
              <Box
                component="input"
                id={inputId}
                type={type || 'text'}
                placeholder={placeholder}
                value={form[field] || ''}
                onChange={locked ? undefined : (e) => setForm((p) => ({ ...p, [field]: e.target.value }))}
                readOnly={locked}
                step={step}
                maxLength={maxLength}
                aria-describedby={prefix ? prefixId : undefined}
                sx={{
                  ...inputSx,
                  ...(prefix && { flex: 1, minWidth: 0 }),
                  ...(locked && { color: MUTED, cursor: 'default', '&:focus': {} }),
                }}
              />
            );
            return (
              <Box key={field}>
                <Typography component="label" htmlFor={inputId} sx={miniLabel}>{label}</Typography>
                {textarea ? (
                  <Box
                    component="textarea"
                    id={inputId}
                    placeholder={placeholder}
                    value={form[field] || ''}
                    onChange={(e) => setForm((p) => ({ ...p, [field]: e.target.value }))}
                    rows={3}
                    sx={{ ...inputSx, resize: 'vertical' }}
                  />
                ) : prefix ? (
                  // A fixed label (e.g. "SGD") in front of the box, so people
                  // can see they don't need to type it themselves.
                  <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                    <Box
                      component="span"
                      id={prefixId}
                      sx={{
                        ...labelMono, fontSize: '9px', color: MUTED,
                        display: 'flex', alignItems: 'center', flexShrink: 0,
                        px: '10px',
                        border: `1px solid ${BORDER}`, borderRight: 'none',
                        background: '#fff',
                      }}
                    >
                      {prefix}
                    </Box>
                    {input}
                  </Box>
                ) : input}
              </Box>
            );
          })}

          {saveError && <InlineAlert severity="error">{saveError}</InlineAlert>}

          <Box
            component="button"
            type="button"
            onClick={handleSave}
            disabled={saving}
            sx={{
              mt: 'auto', py: '9px', border: 'none',
              background: saving ? 'rgba(33,131,51,0.5)' : GREEN,
              borderRadius: '2px',
              cursor: saving ? 'not-allowed' : 'pointer',
              ...labelMono, color: '#fff', fontSize: '9px',
              transition: 'background 0.15s',
              '&:hover': { background: saving ? undefined : GREEN_HOVER },
            }}
          >
            {saving ? 'Saving…' : saved ? 'Saved ✓' : 'Save'}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
